import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChoiceCardProps {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description?: string;
  icon?: ReactNode;
  testId?: string;
  /** "radio" for single choice, "checkbox" for a multi-select list. */
  role?: "radio" | "checkbox";
  disabled?: boolean;
}

/** Large tap target used for every one-decision screen (mobile-first, §25). */
export function ChoiceCard({ selected, onSelect, title, description, icon, testId, role = "radio", disabled }: ChoiceCardProps) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={selected}
      onClick={onSelect}
      disabled={disabled}
      data-testid={testId}
      data-selected={selected ? "true" : "false"}
      className={cn(
        "flex w-full items-center gap-4 rounded-2xl border-2 p-4 text-left transition-colors sm:p-5",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-60",
        selected
          ? "border-pink-500 bg-pink-50 dark:bg-pink-950/30"
          : "border-input bg-background hover:border-pink-300 hover:bg-muted/40",
      )}
    >
      {icon && <span className="shrink-0 text-pink-600 dark:text-pink-400">{icon}</span>}
      <span className="min-w-0 flex-1">
        <span className="block text-base font-semibold sm:text-lg">{title}</span>
        {description && <span className="mt-0.5 block text-sm text-muted-foreground">{description}</span>}
      </span>
      <span
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center border-2",
          role === "radio" ? "rounded-full" : "rounded-md",
          selected ? "border-pink-500 bg-pink-500 text-white" : "border-muted-foreground/40",
        )}
        aria-hidden="true"
      >
        {selected && <Check className="h-4 w-4" />}
      </span>
    </button>
  );
}
